import React, { useCallback, useReducer, useRef, useEffect } from 'react';

import { Editor, Range, Transforms, Element, Text } from 'slate';
import { ReactEditor } from 'slate-react';

import { suggestionsReducer, initialSuggestionState } from './context';
import { determineSuggestionContext } from './utils';
import { useDebounce } from '../../../../../hooks/useDebounce';
import {
  OPERATOR_SUGGESTIONS,
  LOGICAL_OPERATOR_SUGGESTIONS,
  GROUPING_OPERATOR_SUGGESTIONS,
  OPERATOR_TAKES_PARENTHESES,
  operatorsByDataTypeMap,
  PREFIX_OPERATOR_SUGGESTIONS,
} from '../../constants';
import { CustomEditor, FieldOption, ValueOption } from '../../types';
import { getTypeOfPreviousElement, parseExpression, conditionallyQuoteWrap } from '../../utils';

interface SuggestionGroup {
  id?: string;
  isLoading: boolean;
  options: string[];
  type: string;
}

/**
 * Main suggestions hook
 */
export const useSuggestions = ({
  editor,
  fieldOptions,
  getOperatorSuggestions,
  isLoadingFieldOptions,
  isLoadingValueSuggestions,
  menuListRef,
  prioritizedCloudFields = new Set(),
  updateFieldOptions,
  updateValueSuggestions,
  valueSuggestions,
}: {
  editor: CustomEditor;
  fieldOptions: FieldOption[];
  getOperatorSuggestions?: (fieldName: string) => string[];
  isLoadingFieldOptions?: boolean;
  isLoadingValueSuggestions?: boolean;
  menuListRef: React.RefObject<HTMLElement | null>;
  prioritizedCloudFields?: Set<string>;
  updateFieldOptions?: (inputText?: string) => void;
  updateValueSuggestions?: (fieldName: string, inputText?: string) => void;
  valueSuggestions?: ValueOption[];
}) => {
  const [state, dispatch] = useReducer(suggestionsReducer, initialSuggestionState);
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  const fieldSuggestions = fieldOptions?.map((field) => ({
    name: field.name,
    type: field.kind,
  }));

  const dispatchCountRef = useRef({ ['' as string]: 0 });

  const debugDispatch = useCallback((action: any) => {
    const actionType = action.type || 'unknown';
    dispatchCountRef.current[actionType] = (dispatchCountRef.current[actionType] || 0) + 1;

    dispatch(action);
  }, []);

  const { debouncedFunc: debouncedUpdateValueSuggestions } = useDebounce(
    (fieldName: string, inputText: string) => {
      if (updateValueSuggestions) {
        const cleanedInputText = inputText.trim().replaceAll(/^['"]|['"]$/g, '');
        updateValueSuggestions(fieldName, cleanedInputText);
      }
    },
    200,
  );

  const { debouncedFunc: debouncedUpdateFieldOptions } = useDebounce((inputText: string) => {
    if (updateFieldOptions) {
      updateFieldOptions(inputText);
    }
  }, 200);

  /**
   * Update suggestions based on cursor position and text
   */
  const updateSuggestionContextAndFetch = useCallback(() => {
    const { selection } = editor;
    if (!selection) {
      return;
    }

    try {
      const [start] = Range.edges(selection);
      const lineRange = Editor.range(editor, Editor.start(editor, start.path), start);
      const lineText = Editor.string(editor, lineRange);

      const previousType = getTypeOfPreviousElement(editor, start.path);

      const { context, field, inputText, operator, prefixOperator } = determineSuggestionContext(
        lineText,
        previousType,
      );

      const newContext = context;
      const newField = field;
      const newOperator = operator;
      const newInputText = inputText;
      const newPrefixOperator = prefixOperator;
      const contextTypeChanged = newContext !== state.suggestionContext;
      const fieldChanged = newField !== state.currentField;
      const operatorChanged = newOperator !== state.currentOperator;
      const inputTextChanged = newInputText !== state.currentContextualInputText;
      const prefixOperatorChanged = newPrefixOperator !== state.currentPrefixOperator;
      const overallContextChanged =
        contextTypeChanged ||
        fieldChanged ||
        operatorChanged ||
        inputTextChanged ||
        prefixOperatorChanged;

      if (newContext === 'value' && newField && updateValueSuggestions) {
        if (contextTypeChanged) {
          debouncedUpdateValueSuggestions(newField, newInputText);
        } else if (inputTextChanged && state.currentField) {
          debouncedUpdateValueSuggestions(state.currentField, newInputText);
        }
      } else if (newContext === 'field' && updateFieldOptions) {
        debouncedUpdateValueSuggestions(newInputText, '');
        debouncedUpdateFieldOptions(newInputText);
      }

      if (overallContextChanged) {
        debugDispatch({
          context: newContext,
          field: newField,
          inputText: newInputText,
          operator: newOperator,
          prefixOperator: newPrefixOperator,
          type: 'SET_CONTEXT',
        });
      }
    } catch {
      debugDispatch({
        context: null,
        field: null,
        inputText: '',
        operator: null,
        prefixOperator: null,
        type: 'SET_CONTEXT',
      });
    }
  }, [
    editor,
    updateValueSuggestions,
    updateFieldOptions,
    debouncedUpdateValueSuggestions,
    debouncedUpdateFieldOptions,
    debugDispatch,
    state.suggestionContext,
    state.currentField,
    state.currentOperator,
    state.currentContextualInputText,
    state.currentPrefixOperator,
  ]);

  /**
   * Generate filtered suggestions based on context and prefix
   */
  useEffect(() => {
    if (!state.suggestionContext) {
      if (state.suggestions.length > 0) {
        debugDispatch({ suggestions: [], type: 'SET_SUGGESTIONS' });
      }

      return;
    }

    try {
      const newSuggestions: Array<{
        id: string;
        isLoading: boolean;
        options: string[];
        type: string;
      }> = [];
      const prefixWithoutQuotes = state.currentContextualInputText.replaceAll(/^['"]|['"]$/g, '');

      const filterMatchesPrefix = (item: string) => {
        const cleanPrefix = prefixWithoutQuotes.toLowerCase();

        return item.toLowerCase().startsWith(cleanPrefix);
      };

      switch (state.suggestionContext) {
        case 'logical-operator': {
          const filteredLogicalOperators = LOGICAL_OPERATOR_SUGGESTIONS.filter((op) =>
            op.toUpperCase().includes(prefixWithoutQuotes.toUpperCase()),
          ).map((op) => `${op} `);

          if (filteredLogicalOperators.length > 0) {
            newSuggestions.push({
              id: 'logical-operator',
              isLoading: false,
              options: filteredLogicalOperators,
              type: 'logical-operator',
            });
          }

          const filteredGroupingOperators = GROUPING_OPERATOR_SUGGESTIONS.filter((op) =>
            op.includes(prefixWithoutQuotes),
          );

          if (filteredGroupingOperators.length > 0) {
            newSuggestions.push({
              id: 'grouping-operator',
              isLoading: false,
              options: filteredGroupingOperators,
              type: 'grouping-operator',
            });
          }

          break;
        }
        case 'field': {
          const allFieldNames = fieldSuggestions?.map((field) => field.name) || [];

          const sortedResults = allFieldNames.sort((a, b) => {
            const aIsPrioritized = prioritizedCloudFields.has(a);
            const bIsPrioritized = prioritizedCloudFields.has(b);
            if (aIsPrioritized === bIsPrioritized) {
              return 0;
            } else if (aIsPrioritized) {
              return -1;
            } else {
              return 1;
            }
          });

          if (sortedResults.length > 0) {
            newSuggestions.push({
              id: 'field',
              isLoading: isLoadingFieldOptions || false,
              options: sortedResults,
              type: 'field',
            });
          }

          const filteredGroupingOperators = GROUPING_OPERATOR_SUGGESTIONS.filter((op) =>
            op.includes(state.currentContextualInputText),
          );

          if (filteredGroupingOperators.length > 0) {
            newSuggestions.push({
              id: 'grouping-operator',
              isLoading: false,
              options: filteredGroupingOperators,
              type: 'grouping-operator',
            });
          }

          const filteredPrefixOperators = PREFIX_OPERATOR_SUGGESTIONS.filter((op) =>
            op.toLowerCase().includes(state.currentContextualInputText.toLowerCase()),
          );

          if (filteredPrefixOperators.length > 0) {
            newSuggestions.push({
              id: 'prefix-operator',
              isLoading: false,
              options: filteredPrefixOperators,
              type: 'prefix-operator',
            });
          }

          break;
        }

        case 'operator': {
          let availableOperators: string[] = OPERATOR_SUGGESTIONS;

          const currentFieldDefinition = fieldOptions?.find(
            (field) => field.name === state.currentField,
          );
          const currentFieldType = currentFieldDefinition?.kind;

          if (currentFieldType && operatorsByDataTypeMap[currentFieldType]) {
            availableOperators = operatorsByDataTypeMap[currentFieldType];
          }

          if (getOperatorSuggestions && state.currentField) {
            try {
              const specificOperators = getOperatorSuggestions(state.currentField);
              if (specificOperators && specificOperators.length > 0) {
                availableOperators = specificOperators;
              }
            } catch {
              // Handle errors silently if prop function fails
            }
          }

          const filteredOps = availableOperators.filter((item) => filterMatchesPrefix(item));
          if (filteredOps.length > 0 || isLoadingValueSuggestions) {
            newSuggestions.push({
              id: 'operator',
              isLoading: false,
              options: filteredOps,
              type: 'operator',
            });
          }
          break;
        }

        case 'value': {
          if (
            OPERATOR_TAKES_PARENTHESES[
              state.currentOperator as keyof typeof OPERATOR_TAKES_PARENTHESES
            ]
          ) {
            newSuggestions.push({
              id: 'grouping-operator',
              isLoading: false,
              options: GROUPING_OPERATOR_SUGGESTIONS,
              type: 'grouping-operator',
            });
          }
          const currentValues = valueSuggestions || [];
          const filteredValueOptions = currentValues.map((opt) => opt.name);

          newSuggestions.push({
            id: 'value',
            isLoading: isLoadingValueSuggestions || false,
            options: filteredValueOptions,
            type: 'value',
          });

          break;
        }
      }

      if (JSON.stringify(newSuggestions) !== JSON.stringify(state.suggestions)) {
        dispatch({ suggestions: newSuggestions, type: 'SET_SUGGESTIONS' });
      }
    } catch {
      // Handle errors silently
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.suggestionContext,
    state.currentContextualInputText,
    state.currentField,
    fieldSuggestions,
    valueSuggestions,
    isLoadingValueSuggestions,
    getOperatorSuggestions,
    editor,
    dispatch,
    debugDispatch,
  ]);

  const applySuggestion = useCallback(
    (editor: CustomEditor, type: string, suggestion: string) => {
      if (!editor.selection) {
        ReactEditor.focus(editor);
      }

      Editor.withoutNormalizing(editor, () => {
        const { selection } = editor;
        if (!selection || !Range.isRange(selection)) {
          return;
        }

        const [nodeEntry] = Editor.nodes(editor, {
          at: selection.anchor,
          match: (n) => Text.isText(n) || Element.isElement(n),
          mode: 'lowest',
        });

        if (!nodeEntry) return;

        const [currentNode, nodePath] = nodeEntry;

        const nodeText = Text.isText(currentNode)
          ? currentNode.text
          : Editor.string(editor, nodePath);
        const [[prefixOperator, field, operator, value], meta] = parseExpression(nodeText ?? '');

        const isNewExpression = type === 'logical-operator' || type === 'prefix-operator';
        const isInListValue =
          type === 'value' &&
          OPERATOR_TAKES_PARENTHESES[operator as keyof typeof OPERATOR_TAKES_PARENTHESES];
        const isGroupingOperatorOpener =
          type === 'grouping-operator' &&
          OPERATOR_TAKES_PARENTHESES[operator as keyof typeof OPERATOR_TAKES_PARENTHESES];
        const isInOperator =
          OPERATOR_TAKES_PARENTHESES[suggestion as keyof typeof OPERATOR_TAKES_PARENTHESES];
        const isDefaultHandling = prefixOperator || field || operator || value;

        let newExpression = '';
        if (isNewExpression) {
          newExpression = `${suggestion} `;
        } else if (isInListValue) {
          const splitValue = value?.split(',').map((v) => v.trim().replaceAll('"', '')) ?? [];
          const filtered = splitValue.filter((v) => v !== meta.contextInput);

          filtered.push(suggestion);
          const newValueContents = filtered?.map((v) => `"${v}"`).join(', ') ?? '';
          const newValue = `(${newValueContents},${value?.includes(')') ? ')' : ''}`;
          newExpression = [prefixOperator, field, operator, newValue].filter(Boolean).join(' ');
        } else if (isGroupingOperatorOpener) {
          newExpression =
            suggestion === GROUPING_OPERATOR_SUGGESTIONS[0]
              ? [prefixOperator, field, operator, `(`].filter(Boolean).join(' ')
              : [prefixOperator, field, operator, `(${value})`].filter(Boolean).join(' ');
        } else if (isInOperator) {
          newExpression = [prefixOperator, field, suggestion, `(`].join(' ');
        } else if (isDefaultHandling) {
          newExpression = [
            type === 'prefix-operator' ? suggestion : prefixOperator,
            type === 'field' ? suggestion : field,
            type === 'operator' ? suggestion : operator,
            type === 'value' ? conditionallyQuoteWrap(suggestion) : value,
          ]
            .filter(Boolean)
            .join(' ');
        } else {
          newExpression = suggestion;
        }

        const nodeRange = Editor.range(editor, nodePath);

        Transforms.select(editor, nodeRange);

        Transforms.delete(editor);
        Transforms.insertText(editor, `${newExpression} `);
      });

      setTimeout(() => {
        updateSuggestionContextAndFetch();
        Editor.normalize(editor, { force: true });
      }, 50);

      try {
        ReactEditor.focus(editor);
      } catch {
        // Handle errors silently
      }
    },
    [updateSuggestionContextAndFetch],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();

          if (ReactEditor.isFocused(editor)) {
            event.preventDefault();
            const menuListElement = menuListRef.current;
            if (menuListElement) {
              const firstItem = menuListElement.querySelector<HTMLElement>(
                '[role="menuitem"]:not([disabled])',
              );
              if (firstItem) {
                setTimeout(() => {
                  firstItem.focus();
                }, 0);
              }
            }
          }
          break;
        }

        case 'Enter':
        case 'Tab': {
          if (
            !ReactEditor.isFocused(editor) &&
            menuListRef.current?.contains(document.activeElement)
          ) {
            const focusedElement = document.activeElement as HTMLElement;
            let selectedOption: string | null = null;
            let selectedType: string | null = null;
            let itemIndex = -1;

            const allItems = [
              ...menuListRef.current.querySelectorAll<HTMLElement>(
                '[role="menuitem"]:not([disabled])',
              ),
            ];
            itemIndex = allItems.indexOf(focusedElement);

            if (itemIndex !== -1) {
              let currentCount = 0;
              for (const group of state.suggestions as SuggestionGroup[]) {
                const groupHasRenderedItems = !group.isLoading && group.options.length > 0;
                if (groupHasRenderedItems) {
                  if (
                    itemIndex >= currentCount &&
                    itemIndex < currentCount + group.options.length
                  ) {
                    selectedOption = group.options[itemIndex - currentCount];
                    selectedType = group.type;
                    break;
                  }
                  currentCount += group.options.length;
                }
              }
            }

            if (selectedOption && selectedType) {
              event.preventDefault();
              applySuggestion(editor, selectedType, selectedOption);
            } else {
              dispatch({ type: 'SET_POPOVER_VISIBILITY', visible: false });
            }
          }
          break;
        }
      }
    },
    [state.suggestions, applySuggestion, editor, dispatch, menuListRef],
  );

  return {
    applySuggestion,
    currentContextualInputText: state.currentContextualInputText,
    currentFieldObject: state.currentFieldObject,
    handleKeyDown,
    isLoadingSuggestions:
      !!(state.suggestionContext === 'value' && isLoadingValueSuggestions) ||
      !!(state.suggestionContext === 'field' && isLoadingFieldOptions),
    suggestionContext: state.suggestionContext,
    suggestions: state.suggestions,
    updateSuggestionContextAndFetch,
  };
};

export { type FieldOption } from '../../types';
